import type { PositionBracket } from '@flipster/apis/v2/snapshot';
import type { Position } from '@flipster/r230522/original/position';
import Num, { BigNumber } from '@flipster/utils/num';
import type { OrderMarginType, V2OrderSideType } from '@flipster/utils/types/base';

import { calculateLiqPrice } from './calc';
import type { InternalPosition } from './internal-types';
import { computeMaintAmounts } from './maint-amount';
import type { BracketTierWithMaintAmount } from './maint-amount';
import { toFiniteDecimal, toNonNegativeDecimal, toPositiveDecimal } from './number-guards';
import { simulateOrderToPosition } from './sim';

/**
 * The order being previewed. Field names mirror the WS-published `Order`
 * (r230522) so call sites can read straight off form state without renaming.
 *
 * `qty` and `price` are required here even though they're optional on `Order`,
 * because the calc has no fallback when either is missing.
 *
 * Two fields aren't on the `Order` payload and have to be wired in by the
 * caller: `feeRate` (max of pswap taker/maker rates) and `marginMode` (from
 * the form's margin-mode selector or the parent position's margin type).
 */
export interface OrderInput {
  side: V2OrderSideType;
  qty: string;
  price: string;
  initLeverage: number;
  feeRate: string;
  marginMode: OrderMarginType;
}

export interface EstimateOrderLiqPriceArgs {
  /**
   * Existing live position the order is being applied against. Pass `null`
   * when opening a brand-new slot (multi-position cross/isolated, or no prior
   * one-way position on this symbol).
   */
  targetPosition: Position | null;
  /**
   * Order being previewed. Pass `null` to compute the *current* liq price of
   * `targetPosition` without applying any fill.
   */
  order: OrderInput | null;
  /** Mark price for `targetSymbol`, joined from the market topic at the call site. */
  markPrice: string;
  /** Symbol the order is targeting; used as fallback when `targetPosition` is null. */
  targetSymbol: string;
  /**
   * Cross margin **balance** for the relevant quote currency
   * (`private/margins.marginBalance`) — wallet + cross uPnL − reserved-for-isolated,
   * matching prex-core's `cross_margin_balance` used by the liquidation engine.
   * Must NOT be `crossMarginAvailable`, which nets out reserved initial margin
   * (`marginBalance − initMarginReserved`) and would inflate the liq price.
   */
  crossMarginBalance: string;
  /**
   * Aggregate `maint_position_margin` for the relevant quote currency
   * (`private/margins.maintPositionMargin`). Treated as missing when `null`
   * or `undefined` — the estimator returns `null` so the UI renders `—`.
   */
  maintPositionMargin: string | null | undefined;
  /** Tick size of the target symbol (`Instrument.tickSize`). */
  tickSize: string;
  /** Resolved bracket spec for the target symbol (`PositionBracket.entries`). */
  positionBracket: Pick<PositionBracket, 'entries'>;
}

const toInternalPosition = (position: Position | null, markPrice: string): InternalPosition | null => {
  if (!position) return null;

  return {
    positionId: position.positionId,
    symbol: position.symbol,
    position: position.position,
    avgEntryPrice: position.avgEntryPrice,
    marginType: position.marginType,
    initLeverage: position.initLeverage,
    isolatedMarginInitial: position.isolatedMarginInitial,
    isolatedMarginAdded: position.isolatedMarginAdded,
    markPrice,
    walletRealization: '0',
  };
};

const findTierByNotional = (notional: BigNumber, tiers: BracketTierWithMaintAmount[]) => {
  const tier = tiers.find((entry) => Num(entry.notionalPositionSizeUpperLimit).gte(notional));
  return tier ?? tiers.at(-1) ?? null;
};

const calculatePositionMaintMargin = (position: InternalPosition, tiers: BracketTierWithMaintAmount[]) => {
  const signedSize = toFiniteDecimal(position.position);
  const markPrice = toPositiveDecimal(position.markPrice);

  if (!signedSize || !markPrice || signedSize.eq(0)) return null;

  const notional = signedSize.abs().multipliedBy(markPrice);
  const tier = findTierByNotional(notional, tiers);

  if (!tier) return null;

  return notional.multipliedBy(tier.maintMarginRate).minus(tier.maintAmount);
};

const getPostFillMaintPositionMargin = (
  args: EstimateOrderLiqPriceArgs,
  tiers: BracketTierWithMaintAmount[],
  preFillTarget: InternalPosition | null,
  postFillPosition: InternalPosition,
) => {
  // For isolated, MM aggregate is not consumed by the calc. Return a stable '0'.
  if (postFillPosition.marginType !== 'Cross') return '0';

  const maintPositionMargin = toNonNegativeDecimal(args.maintPositionMargin);
  if (!maintPositionMargin) return null;

  // No order: the existing position is already included in the aggregate.
  if (!args.order) return maintPositionMargin.toString();
  if (tiers.length === 0) return null;

  const newMaintMargin = calculatePositionMaintMargin(postFillPosition, tiers);
  if (!newMaintMargin) return null;

  // The published aggregate already includes the pre-fill MM of the position
  // we're modifying; subtract it before adding the post-fill MM so we don't
  // double-count.
  const oldMaintMargin =
    preFillTarget?.marginType === 'Cross' ? calculatePositionMaintMargin(preFillTarget, tiers) : Num('0');

  if (!oldMaintMargin) return null;

  return maintPositionMargin.minus(oldMaintMargin).plus(newMaintMargin).toString();
};

/**
 * Unified post-fill estimated liquidation price for both Isolated and Cross
 * margin. Mirrors prex-core's `calculate_liq_price` for the simulated post-fill
 * snapshot, so it stays in lockstep with the engine that actually liquidates.
 *
 * Returns `null` whenever inputs are insufficient to produce a meaningful
 * estimate (callers render that as `—`):
 *  - missing `maintPositionMargin` (cross, before the WS field rolls out)
 *  - bad / zero / non-finite numeric inputs
 *  - bracket walk has no positive-price solution
 *  - solution is on the wrong side of mark price (already past liquidation)
 */
export function estimateOrderLiqPrice(args: EstimateOrderLiqPriceArgs): string | null {
  const tiers = computeMaintAmounts(args.positionBracket.entries);
  if (tiers.length === 0) return null;

  const preFillTarget = toInternalPosition(args.targetPosition, args.markPrice);
  const postFillPosition = args.order
    ? simulateOrderToPosition(preFillTarget, args.order, args.targetSymbol)
    : preFillTarget;

  if (!postFillPosition) return null;

  const maintPositionMargin = getPostFillMaintPositionMargin(args, tiers, preFillTarget, postFillPosition);
  if (maintPositionMargin === null) return null;

  return calculateLiqPrice({
    crossMarginBalance: args.crossMarginBalance,
    maintPositionMargin,
    targetPosition: postFillPosition,
    tickSize: args.tickSize,
    tiers,
  });
}
